import { getDataClient } from "@/lib/dataClient";
import { getCurrentUserId, useDataQuery } from "@/hooks/useDataQuery";

// Öğretmen paneli — atanmış öğrencilere dayalı veri katmanı.
// Öğretmen, kendisine atanmış (profiles.teacher_id) öğrencilerin verilerini okur.

export interface TeacherStudent {
  id: string;
  userId: string;
  fullName: string;
  grade: string | null;
  city: string | null;
}

export function useTeacherStudents() {
  return useDataQuery<TeacherStudent[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("profiles")
      .select("id, user_id, full_name, grade, city")
      .eq("teacher_id", userId)
      .eq("role", "ogrenci")
      .order("full_name", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(
      (p: { id: string; user_id: string | null; full_name: string | null; grade: string | null; city: string | null }) => ({
        id: p.id,
        userId: p.user_id ?? p.id,
        fullName: p.full_name ?? "Öğrenci",
        grade: p.grade ?? null,
        city: p.city ?? null,
      }),
    );
  });
}

export async function assignStudent(
  studentUserId: string,
): Promise<{ ok: boolean; message?: string; studentName?: string }> {
  const dataClient = getDataClient();
  const { data, error } = await dataClient.rpc("assign_student_to_teacher", {
    p_student_user_id: studentUserId,
  });
  if (error) return { ok: false, message: error.message };
  const r = (data ?? {}) as { ok?: boolean; student_name?: string; message?: string };
  return { ok: !!r.ok, message: r.message, studentName: r.student_name };
}

export async function unassignStudent(studentUserId: string): Promise<void> {
  const dataClient = getDataClient();
  await dataClient.rpc("unassign_student", { p_student_user_id: studentUserId });
}

export interface HomeworkInput {
  studentIds: string[];
  title: string;
  subject: string;
  dueDate: string;
  description: string;
}

export async function createHomework(
  input: HomeworkInput,
): Promise<{ ok: boolean; count?: number; message?: string }> {
  const dataClient = getDataClient();
  const { data, error } = await dataClient.rpc("create_homework_for_students", {
    p_student_ids: input.studentIds,
    p_title: input.title,
    p_subject: input.subject,
    p_due_date: input.dueDate,
    p_description: input.description,
  });
  if (error) return { ok: false, message: error.message };
  const r = (data ?? {}) as { ok?: boolean; count?: number; message?: string };
  return { ok: !!r.ok, count: r.count, message: r.message };
}

export interface TeacherHomework {
  id: string;
  studentName: string;
  title: string;
  subject: string;
  dueDate: string;
  status: string;
}

export const TEACHER_HOMEWORK_STATUS_LABELS: Record<string, string> = {
  bekliyor: "Bekliyor",
  tamamlandi: "Tamamlandı",
  gecikti: "Gecikti",
};

export const TEACHER_HOMEWORK_STATUS_COLORS: Record<string, string> = {
  bekliyor: "bg-secondary-100 text-secondary-800",
  tamamlandi: "bg-accent-100 text-accent-800",
  gecikti: "bg-red-50 text-red-700",
};

export function useTeacherHomework() {
  return useDataQuery<TeacherHomework[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("homework")
      .select("id, user_id, title, subject, due_date, status")
      .eq("teacher_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;

    const rows = (data ?? []) as {
      id: string;
      user_id: string;
      title: string;
      subject: string;
      due_date: string | null;
      status: string;
    }[];

    const studentIds = Array.from(new Set(rows.map((r) => r.user_id)));
    let nameMap = new Map<string, string>();
    if (studentIds.length > 0) {
      const { data: profiles, error: pErr } = await dataClient
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", studentIds);
      if (!pErr) {
        (profiles ?? []).forEach((p: { user_id: string | null; full_name: string | null }) => {
          if (p.user_id) nameMap.set(p.user_id, p.full_name ?? "Öğrenci");
        });
      }
    }

    return rows.map((r) => ({
      id: r.id,
      studentName: nameMap.get(r.user_id) ?? "Öğrenci",
      title: r.title,
      subject: r.subject,
      dueDate: r.due_date ?? "",
      status: r.status,
    }));
  });
}

// ---------- Öğrenciye özel analiz verileri ----------

export interface TeacherExamResult {
  id: string;
  name: string;
  date: string;
  correct: number;
  wrong: number;
  blank: number;
  net: number;
}

function fullDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

export function useTeacherStudentExams(studentUserId: string | null) {
  return useDataQuery<TeacherExamResult[]>(
    async () => {
      if (!studentUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("exam_attempts")
        .select("id, exam_name, correct_count, wrong_count, blank_count, net, taken_on")
        .eq("user_id", studentUserId)
        .order("taken_on", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(
        (e: { id: string; exam_name: string; correct_count: number; wrong_count: number; blank_count: number; net: number; taken_on: string }) => ({
          id: e.id,
          name: e.exam_name,
          date: fullDate(e.taken_on),
          correct: e.correct_count ?? 0,
          wrong: e.wrong_count ?? 0,
          blank: e.blank_count ?? 0,
          net: Number(e.net ?? 0),
        }),
      );
    },
    [studentUserId],
  );
}

export interface TeacherSubjectPerformance {
  subject: string;
  net: number;
  maxNet: number;
}

export function useTeacherStudentPerformance(studentUserId: string | null) {
  return useDataQuery<TeacherSubjectPerformance[]>(
    async () => {
      if (!studentUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("subject_performance")
        .select("subject, net, max_net")
        .eq("user_id", studentUserId);
      if (error) throw error;
      return (data ?? []).map(
        (s: { subject: string; net: number; max_net: number }) => ({
          subject: s.subject,
          net: Number(s.net ?? 0),
          maxNet: Number(s.max_net ?? 0),
        }),
      );
    },
    [studentUserId],
  );
}

export interface TeacherWeakTopic {
  id: string;
  subject: string;
  topic: string;
  mastery: number;
}

export function useTeacherStudentWeakTopics(studentUserId: string | null) {
  return useDataQuery<TeacherWeakTopic[]>(
    async () => {
      if (!studentUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("weak_topics")
        .select("id, subject, topic, mastery_percent")
        .eq("user_id", studentUserId)
        .order("mastery_percent", { ascending: true })
        .limit(10);
      if (error) throw error;
      return (data ?? []).map(
        (w: { id: string; subject: string; topic: string; mastery_percent: number }) => ({
          id: w.id,
          subject: w.subject,
          topic: w.topic,
          mastery: Number(w.mastery_percent ?? 0),
        }),
      );
    },
    [studentUserId],
  );
}

export interface TeacherAiProfile {
  summary: string;
  learningLevel: string;
  motivationLevel: number;
  strongSubjects: string[];
  weakSubjects: string[];
  recentNotes: { type: string; content: string; date: string }[];
}

function shortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
}

export function useTeacherStudentAiProfile(studentUserId: string | null) {
  return useDataQuery<TeacherAiProfile>(
    async () => {
      if (!studentUserId) {
        return {
          summary: "",
          learningLevel: "",
          motivationLevel: 0,
          strongSubjects: [],
          weakSubjects: [],
          recentNotes: [],
        };
      }
      const dataClient = getDataClient();

      const [profile, memory] = await Promise.all([
        dataClient
          .from("student_ai_profile")
          .select("ai_summary, learning_level, motivation_level, strong_subjects, weak_subjects")
          .eq("user_id", studentUserId)
          .maybeSingle(),
        dataClient
          .from("student_ai_memory")
          .select("content, memory_type, created_at")
          .eq("user_id", studentUserId)
          .order("created_at", { ascending: false })
          .limit(10),
      ]);

      if (profile.error) throw profile.error;
      if (memory.error) throw memory.error;

      const prof = (profile.data ?? {}) as {
        ai_summary: string | null;
        learning_level: string | null;
        motivation_level: number | null;
        strong_subjects: string[] | null;
        weak_subjects: string[] | null;
      };

      const strong = Array.isArray(prof.strong_subjects)
        ? prof.strong_subjects.filter((s): s is string => typeof s === "string")
        : [];
      const weak = Array.isArray(prof.weak_subjects)
        ? prof.weak_subjects.filter((s): s is string => typeof s === "string")
        : [];

      const recentNotes = (memory.data ?? []).map(
        (m: { content: string; memory_type: string | null; created_at: string }) => ({
          type: m.memory_type ?? "not",
          content: m.content,
          date: shortDate(m.created_at),
        }),
      );

      return {
        summary: prof.ai_summary ?? "",
        learningLevel: prof.learning_level ?? "",
        motivationLevel: Number(prof.motivation_level ?? 0),
        strongSubjects: strong,
        weakSubjects: weak,
        recentNotes,
      };
    },
    [studentUserId],
  );
}